import {useEffect, useState} from "react";
import {FieldError, useWatch} from "react-hook-form";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";

import {mb, mt, p} from "@pg-design/helpers-css-strings";
import {PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {CheckboxControlled} from "@pg-design/rhf-components-module";
import {InputField} from "@pg-design/rhf-components-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FOR_YOU_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {authSucceeded} from "../../../auth/actions/auth_succeeded_event";
import {AuthActionType} from "../../../auth/types/AuthActionType";
import {LeadButtonTerms} from "../../../lead/components/LeadButtonTerms";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useCreateForYouClientMutation, useUpdateForYouClientMutation} from "../../api/for_you_client";
import {setForYouUser} from "../../state/for_you_modal_slice";
import {ForYouFormDisplayGtmEvent, ForYouSendGtmEvent} from "../../tracking/for_you_forms_gtm_events";
import {ForYouDisplayActions} from "../../types/ForYouDisplayActions";
import {ForYouEventType} from "../../types/ForYouEventType";
import {SubmitHandlerType} from "../../types/SubmitHandlerType";
import {getExistingUserPostData} from "../../utils/get_existing_user_update_data";
import {getPostData} from "../../utils/get_post_data";
import {ForYouModalButton} from "../modal_parts/ForYouModalButton";

interface IProps {
    submitHandler: SubmitHandlerType;
    onNextStep: () => void;
    onSubmitError?: (name: string, error: FieldError) => void;
}

const buttonText = "Zakończ i wyślij";

export const ForYouUserForm = (props: IProps) => {
    const dispatch = useAppDispatch();

    const modalState = useAppSelector((state) => state.forYouModal);
    const codes = useSelector((state: IRPStore) => state.variables.data?.country_codes);

    const {data: user} = useGetUserProfileQuery();
    const {isMobile} = useUserDevice();
    const [post, {isError, isSuccess, error}] = useCreateForYouClientMutation();
    const [put, updateState] = useUpdateForYouClientMutation();

    const name = useWatch({name: "name"});
    const email = useWatch({name: "email"});
    const phone = useWatch({name: "phone"});
    const marketingConsent = useWatch({name: "marketing_consent"});

    const [allowPost, setAllowPost] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        ForYouFormDisplayGtmEvent({event: ForYouEventType.USER, action: ForYouDisplayActions.OPEN, region: modalState.mainRegionName});
    }, []);

    useEffect(() => {
        if (allowPost) {
            onAfterSubmit();
        }
    }, [allowPost]);

    useEffect(() => {
        if (isError && "data" in error) {
            setLoading(false);
            setAllowPost(false);
            const errorData = error.data as {[key: string]: string[]};
            let isPhoneError = false;
            let isNameError = false;

            if ("phone" in errorData) {
                props.onSubmitError?.("phone", {type: "error", message: errorData.phone[0]});
                isPhoneError = true;
            }

            if ("name" in errorData) {
                props.onSubmitError?.("name", {type: "error", message: errorData.name[0]});
                isNameError = true;
            }

            if ("email" in errorData) {
                const checkIfCanUpdate = Object.keys(errorData).length === 1 && (updateState.isUninitialized || updateState.isError);
                const isEmailUnique = errorData.email[0] !== "Wartość dla tego pola musi być unikalna.";
                if (!isEmailUnique && checkIfCanUpdate && !isPhoneError && !isNameError) {
                    const updateData = getExistingUserPostData(modalState);
                    put(updateData);
                    return;
                }
                if (isEmailUnique) {
                    props.onSubmitError?.("email", {type: "error", message: errorData.email[0]});
                }
            }
        }
    }, [isError]);

    useEffect(() => {
        setLoading(false);
        setAllowPost(false);

        //TODO Fix this, it has no login or registration logic, and handleAfterAuth is not used correctly (name points to "register after registration")
        const didLogin = false;
        if (isSuccess && didLogin) {
            singUpAfterRejestration();
        }

        if (isSuccess || updateState.isSuccess) {
            ForYouSendGtmEvent();
            props.onNextStep();
            return;
        }

        if (updateState.isError && "data" in updateState.error) {
            const updateError = updateState.error.data as {[key: string]: string};
            if (
                "detail" in updateError &&
                (updateError.detail === "Nie podano danych uwierzytelniających." || updateError.detail === "Client not found for the authenticated user.")
            ) {
                dispatch(addWarningNotification(`Zaloguj się na konto ${email}, aby wysłać zgłoszenie.`, 5000));
                return;
            }
            dispatch(addWarningNotification("Twoje zgłoszenie nie zostało wysłane. Spróbuj ponownie póżniej.", 3000));
        }
    }, [isSuccess, updateState]);

    const singUpAfterRejestration = async () => {
        dispatch(
            authSucceeded({
                type: AuthActionType.REGISTRATION,
                userSource: UserSource.USER_SOURCE_CLIENT_SURVEY,
                registrationMethod: "email"
            })
        );
    };

    const onSubmit = async () => {
        setLoading(true);

        await dispatch(setForYouUser({name, email, phone, marketing_consent: marketingConsent}));

        setAllowPost(true);
    };

    const onAfterSubmit = () => {
        const postData = getPostData(modalState);
        post(postData);
    };

    return (
        <form onSubmit={props.submitHandler(onSubmit)} css={formStyle}>
            <InputField name="name" placeholder="Imię i Nazwisko" className="for-you-user-input" dataTestId={FOR_YOU_TEST_IDS.NAME_FIELD} />

            <InputField
                name="email"
                placeholder="e-mail"
                type="email"
                className="for-you-user-input"
                disabled={Boolean(user?.email)}
                dataTestId={FOR_YOU_TEST_IDS.EMAIL_FIELD}
            />

            {codes && (
                <PhoneFieldControlled
                    name="phone"
                    codes={codes}
                    placeholder="Numer telefonu"
                    className="for-you-user-input"
                    maxMenuHeight={isMobile ? 120 : 160}
                    dataTestId={FOR_YOU_TEST_IDS.PHONE_FIELD}
                />
            )}

            <div className="permissions-checkboxes">
                <CheckboxControlled
                    name="marketing_consent"
                    id="marketingConsent"
                    labelContent={
                        <Text variant="info_txt_2">Chcę otrzymywać mailem lub SMSem linki do nowych ofert nieruchomości. Zgodę mogę zawsze odwołać.</Text>
                    }
                    checkedMarkColor="#02D054"
                    dataTestId={FOR_YOU_TEST_IDS.MARKETING_CONSENT}
                />
            </div>

            <ForYouModalButton isLoading={loading} buttonText={buttonText} dataTestId={FOR_YOU_TEST_IDS.FINISH_AND_SEND_BUTTON} />

            <LeadButtonTerms buttonText={buttonText} align="center" css={termsStyle} />
        </form>
    );
};

const formStyle = css`
    width: 100%;
    ${p(3, 1.5, 0, 1.5)};
    background-color: #fff;

    .for-you-user-input {
        ${mt(1.5)};
    }

    .permissions-checkboxes {
        ${mt(2.5)};
        ${mb(3)};
    }

    .selectStyle {
        max-width: 200px;
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        background-color: ${getThemeVariable("colors.gray.100")};
        ${p(8, 5, 8, 5)};
    }
`;

const termsStyle = css`
    display: inline-block;
`;
import {useEffect, useState} from "react";
import {FieldError, useWatch} from "react-hook-form";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";

import {mb, mt, p} from "@pg-design/helpers-css-strings";
import {PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {CheckboxControlled} from "@pg-design/rhf-components-module";
import {InputField} from "@pg-design/rhf-components-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FOR_YOU_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {authSucceeded} from "../../../auth/actions/auth_succeeded_event";
import {AuthActionType} from "../../../auth/types/AuthActionType";
import {LeadButtonTerms} from "../../../lead/components/LeadButtonTerms";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useCreateForYouClientMutation, useUpdateForYouClientMutation} from "../../api/for_you_client";
import {setForYouUser} from "../../state/for_you_modal_slice";
import {ForYouFormDisplayGtmEvent, ForYouSendGtmEvent} from "../../tracking/for_you_forms_gtm_events";
import {ForYouDisplayActions} from "../../types/ForYouDisplayActions";
import {ForYouEventType} from "../../types/ForYouEventType";
import {SubmitHandlerType} from "../../types/SubmitHandlerType";
import {getExistingUserPostData} from "../../utils/get_existing_user_update_data";
import {getPostData} from "../../utils/get_post_data";
import {ForYouModalButton} from "../modal_parts/ForYouModalButton";

interface IProps {
    submitHandler: SubmitHandlerType;
    onNextStep: () => void;
    onSubmitError?: (name: string, error: FieldError) => void;
}

const buttonText = "Zakończ i wyślij";

export const ForYouUserForm = (props: IProps) => {
    const dispatch = useAppDispatch();

    const modalState = useAppSelector((state) => state.forYouModal);
    const codes = useSelector((state: IRPStore) => state.variables.data?.country_codes);

    const {data: user} = useGetUserProfileQuery();
    const {isMobile} = useUserDevice();
    const [post, {isError, isSuccess, error}] = useCreateForYouClientMutation();
    const [put, updateState] = useUpdateForYouClientMutation();

    const name = useWatch({name: "name"});
    const email = useWatch({name: "email"});
    const phone = useWatch({name: "phone"});
    const marketingConsent = useWatch({name: "marketing_consent"});

    const [allowPost, setAllowPost] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        ForYouFormDisplayGtmEvent({event: ForYouEventType.USER, action: ForYouDisplayActions.OPEN, region: modalState.mainRegionName});
    }, []);

    useEffect(() => {
        if (allowPost) {
            onAfterSubmit();
        }
    }, [allowPost]);

    useEffect(() => {
        if (isError && "data" in error) {
            setLoading(false);
            setAllowPost(false);
            const errorData = error.data as {[key: string]: string[]};
            let isPhoneError = false;
            let isNameError = false;

            if ("phone" in errorData) {
                props.onSubmitError?.("phone", {type: "error", message: errorData.phone[0]});
                isPhoneError = true;
            }

            if ("name" in errorData) {
                props.onSubmitError?.("name", {type: "error", message: errorData.name[0]});
                isNameError = true;
            }

            if ("email" in errorData) {
                const checkIfCanUpdate = Object.keys(errorData).length === 1 && (updateState.isUninitialized || updateState.isError);
                const isEmailUnique = errorData.email[0] !== "Wartość dla tego pola musi być unikalna.";
                if (!isEmailUnique && checkIfCanUpdate && !isPhoneError && !isNameError) {
                    const updateData = getExistingUserPostData(modalState);
                    put(updateData);
                    return;
                }
                if (isEmailUnique) {
                    props.onSubmitError?.("email", {type: "error", message: errorData.email[0]});
                }
            }
        }
    }, [isError]);

    useEffect(() => {
        setLoading(false);
        setAllowPost(false);

        //TODO Fix this, it has no login or registration logic, and handleAfterAuth is not used correctly (name points to "register after registration")
        const didLogin = false;
        if (isSuccess && didLogin) {
            singUpAfterRejestration();
        }

        if (isSuccess || updateState.isSuccess) {
            ForYouSendGtmEvent();
            props.onNextStep();
            return;
        }

        if (updateState.isError && "data" in updateState.error) {
            const updateError = updateState.error.data as {[key: string]: string};
            if (
                "detail" in updateError &&
                (updateError.detail === "Nie podano danych uwierzytelniających." || updateError.detail === "Client not found for the authenticated user.")
            ) {
                dispatch(addWarningNotification(`Zaloguj się na konto ${email}, aby wysłać zgłoszenie.`, 5000));
                return;
            }
            dispatch(addWarningNotification("Twoje zgłoszenie nie zostało wysłane. Spróbuj ponownie póżniej.", 3000));
        }
    }, [isSuccess, updateState]);

    const singUpAfterRejestration = async () => {
        dispatch(
            authSucceeded({
                type: AuthActionType.REGISTRATION,
                userSource: UserSource.USER_SOURCE_CLIENT_SURVEY,
                registrationMethod: "email"
            })
        );
    };

    const onSubmit = async () => {
        setLoading(true);

        await dispatch(setForYouUser({name, email, phone, marketing_consent: marketingConsent}));

        setAllowPost(true);
    };

    const onAfterSubmit = () => {
        const postData = getPostData(modalState);
        post(postData);
    };

    return (
        <form onSubmit={props.submitHandler(onSubmit)} css={formStyle}>
            <InputField name="name" placeholder="Imię i Nazwisko" className="for-you-user-input" dataTestId={FOR_YOU_TEST_IDS.NAME_FIELD} />

            <InputField
                name="email"
                placeholder="e-mail"
                type="email"
                className="for-you-user-input"
                disabled={Boolean(user?.email)}
                dataTestId={FOR_YOU_TEST_IDS.EMAIL_FIELD}
            />

            {codes && (
                <PhoneFieldControlled
                    name="phone"
                    codes={codes}
                    placeholder="Numer telefonu"
                    className="for-you-user-input"
                    maxMenuHeight={isMobile ? 120 : 160}
                    dataTestId={FOR_YOU_TEST_IDS.PHONE_FIELD}
                />
            )}

            <div className="permissions-checkboxes">
                <CheckboxControlled
                    name="marketing_consent"
                    id="marketingConsent"
                    labelContent={
                        <Text variant="info_txt_2">Chcę otrzymywać mailem lub SMSem linki do nowych ofert nieruchomości. Zgodę mogę zawsze odwołać.</Text>
                    }
                    checkedMarkColor="#02D054"
                    dataTestId={FOR_YOU_TEST_IDS.MARKETING_CONSENT}
                />
            </div>

            <ForYouModalButton isLoading={loading} buttonText={buttonText} dataTestId={FOR_YOU_TEST_IDS.FINISH_AND_SEND_BUTTON} />

            <LeadButtonTerms buttonText={buttonText} align="center" css={termsStyle} />
        </form>
    );
};

const formStyle = css`
    width: 100%;
    ${p(3, 1.5, 0, 1.5)};
    background-color: #fff;

    .for-you-user-input {
        ${mt(1.5)};
    }

    .permissions-checkboxes {
        ${mt(2.5)};
        ${mb(3)};
    }

    .selectStyle {
        max-width: 200px;
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        background-color: ${getThemeVariable("colors.gray.100")};
        ${p(8, 5, 8, 5)};
    }
`;

const termsStyle = css`
    display: inline-block;
`;
